/**
 * PushNotificationSetup Component
 * UI for enabling/disabling Web Push notifications
 */

import React, { useEffect } from 'react';
import { Button, Alert, Switch, Space, Typography, message } from 'antd';
import { BellOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { notificationsApi } from '@/api/services/notifications.service';
import './PushNotificationSetup.css';

const { Text, Paragraph } = Typography;

interface PushNotificationSetupProps {
  vapidPublicKey?: string;
  onSubscriptionChange?: (subscribed: boolean) => void;
}

export const PushNotificationSetup: React.FC<PushNotificationSetupProps> = ({
  vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '',
  onSubscriptionChange,
}) => {
  const {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    getSubscriptionJson,
  } = usePushNotifications({
    vapidPublicKey,
    onSubscribed: async (sub) => {
      // Send subscription to backend
      const json = sub.toJSON();
      try {
        await notificationsApi.registerPushSubscription(json);
        message.success('Push notifications enabled');
        onSubscriptionChange?.(true);
      } catch (err) {
        console.error('Failed to register push subscription with backend:', err);
        message.error('Failed to register push subscription');
      }
    },
    onUnsubscribed: async () => {
      try {
        await notificationsApi.unregisterPushSubscription();
        message.success('Push notifications disabled');
        onSubscriptionChange?.(false);
      } catch (err) {
        console.error('Failed to unregister push subscription:', err);
      }
    },
  });

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      // Enable push notifications
      if (permission === 'denied') {
        message.warning(
          'Push notifications are blocked. Please enable them in your browser settings.'
        );
        return;
      }

      if (permission === 'default') {
        const granted = await requestPermission();
        if (!granted) {
          return;
        }
      }

      await subscribe();
    } else {
      // Disable push notifications
      await unsubscribe();
    }
  };

  if (!isSupported) {
    return (
      <Alert
        message="Push Notifications Not Supported"
        description="Your browser does not support push notifications."
        type="info"
        showIcon
        icon={<CloseCircleOutlined />}
      />
    );
  }

  if (!vapidPublicKey) {
    return (
      <Alert
        message="Configuration Missing"
        description="VAPID public key not configured. Contact your administrator."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div className="push-notification-setup">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Status */}
        <div className="push-notification-setup-status">
          <Space>
            <BellOutlined style={{ fontSize: '20px' }} />
            <div>
              <Text strong>Web Push Notifications</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {isSubscribed
                  ? 'Enabled - Receive notifications even when not on the site'
                  : 'Disabled - Only receive in-app notifications'}
              </Text>
            </div>
          </Space>
          <Switch
            checked={isSubscribed}
            onChange={handleToggle}
            loading={isLoading}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
          />
        </div>

        {/* Permission status */}
        {permission === 'denied' && (
          <Alert
            message="Push Notifications Blocked"
            description="You have blocked push notifications for this site. To enable them, click the lock icon in your browser's address bar and change the notification permission."
            type="error"
            showIcon
          />
        )}

        {permission === 'default' && !isSubscribed && (
          <Alert
            message="Enable Push Notifications"
            description="Click the switch above to receive notifications even when you're not on the site."
            type="info"
            showIcon
          />
        )}

        {/* Error */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
          />
        )}

        {/* Help text */}
        {isSubscribed && (
          <Paragraph type="secondary" style={{ fontSize: '12px', margin: 0 }}>
            You will receive push notifications for important updates even when
            this tab is closed. You can disable this at any time.
          </Paragraph>
        )}

        {/* Debug info (only in development) */}
        {import.meta.env.DEV && subscription && (
          <Alert
            message="Debug Info"
            description={
              <pre style={{ fontSize: '10px', margin: 0 }}>
                {JSON.stringify(getSubscriptionJson(), null, 2)}
              </pre>
            }
            type="info"
            closable
          />
        )}
      </Space>
    </div>
  );
};
